import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import pluckrLogo from '../assets/images/pluckr-logo.png';
import ThemeToggle from '@/components/ThemeToggle';
import { useThemeMode } from '@/hooks/useThemeMode';

export default function EarlyAccess() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showVerifiedBanner, setShowVerifiedBanner] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { resolvedTheme } = useThemeMode();
  const isDark = resolvedTheme === 'dark';

  useEffect(() => {
    // Check if redirected from email verification
    if (searchParams.get('verified') === 'true') {
      setShowVerifiedBanner(true);

      // Remove query parameter from URL for clean appearance
      searchParams.delete('verified');
      setSearchParams(searchParams, { replace: true });

      // Auto-hide banner after 8 seconds
      setTimeout(() => {
        setShowVerifiedBanner(false);
      }, 8000);
    }
  }, [searchParams, setSearchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || (window.location.hostname === 'localhost' ? 'http://localhost:5000' : 'https://api.pluckr.ai');
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data?.code === 'EMAIL_NOT_VERIFIED' && data?.email) {
          toast.error(data.message || 'Email not verified. Please verify to continue.');
          navigate(`/resend-verification?email=${encodeURIComponent(data.email)}`);
          setIsLoading(false);
          return;
        }
        toast.error(data.message || 'Login failed');
        setIsLoading(false);
        return;
      }

      localStorage.setItem('authToken', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      toast.success('Logged in successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Network error. Please try again.');
      setIsLoading(false);
    }
  };

  const brandGreen = '#9be38f';

  return (
    <div className="min-h-screen w-full bg-background grid-pattern" data-testid="early-access-page">
      {/* Success Banner for Email Verification */}
      {showVerifiedBanner && (
        <div
          className="fixed top-5 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-6 py-4 rounded-xl shadow-lg max-w-[90%]"
          style={{
            backgroundColor: '#22c55e',
            color: '#ffffff',
            animation: 'slideDown 0.3s ease-out',
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <span className="text-[15px] font-medium">
            ✅ Email verified successfully! Please log in to continue.
          </span>
          <button
            onClick={() => setShowVerifiedBanner(false)}
            className="ml-3 text-white/80 hover:text-white text-xl leading-none"
          >
            ×
          </button>
        </div>
      )}

      <style>{`
        @keyframes slideDown {
          from {
            transform: translateX(-50%) translateY(-100px);
            opacity: 0;
          }
          to {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
        }
      `}</style>

      <Toaster
        position="top-right"
        theme={resolvedTheme}
        toastOptions={{
          style: {
            background: isDark ? '#282929' : '#ffffff',
            color: isDark ? '#ffffff' : '#1a1a1a',
            border: isDark ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid #e5e5e5',
          },
        }}
      />

      {/* Fixed header: Brand left, theme toggle right */}
      <div className="fixed top-0 left-0 right-0 z-[70] h-16 flex items-center justify-between px-6 sm:px-8 bg-[var(--pluckr-bg)] border-b border-[var(--pluckr-border)]">
        <Link
          to="/"
          className="inline-flex items-center gap-3 text-black/90 dark:text-white"
          style={{ fontFamily: "'Lora', Georgia, serif" }}
        >
          <img src={pluckrLogo} alt="Pluckr" className="h-9 w-auto" />
          <span className="text-[22px] font-bold tracking-[-0.01em]">Pluckr</span>
        </Link>

        <ThemeToggle />
      </div>

      <div className="max-w-[1200px] mx-auto px-8 pb-16 pt-24">
        <div className="flex flex-col lg:flex-row items-center lg:items-start justify-between gap-14">
          {/* Left headline */}
          <div className="flex-1 w-full lg:pt-10">
            <h1
              className="text-[56px] sm:text-[72px] font-black leading-[0.95] tracking-[-0.03em]"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              <span className="block text-black/80 dark:text-white">Log in to</span>
              <span className="block" style={{ color: brandGreen }}>
                Pluckr
              </span>
            </h1>
          </div>

          {/* Right card */}
          <div className="w-full max-w-[420px]">
            <div
              className="bg-white dark:bg-[#282929] rounded-[18px] shadow-[0_20px_60px_rgba(0,0,0,0.25)] border border-black/5 dark:border-white/15 p-8"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-[12px] font-medium text-black/70 dark:text-white/70">E-mail address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full h-11 rounded-md border border-black/10 dark:border-white/20 bg-white dark:bg-[#282929] text-black dark:text-white px-3 text-[14px] outline-none focus:border-black/20 dark:focus:border-white/30 placeholder:text-black/40 dark:placeholder:text-white/50"
                    placeholder="your email"
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-[12px] font-medium text-black/70 dark:text-white/70">Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full h-11 rounded-md border border-black/10 dark:border-white/20 bg-white dark:bg-[#282929] text-black dark:text-white px-3 text-[14px] outline-none focus:border-black/20 dark:focus:border-white/30 placeholder:text-black/40 dark:placeholder:text-white/50"
                    placeholder="your password"
                    minLength={8}
                    required
                    disabled={isLoading}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full h-11 rounded-full font-semibold text-[13px] text-[#1a1a1a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:brightness-95"
                  style={{ backgroundColor: 'var(--pluckr-green)' }}
                >
                  {isLoading ? 'Logging in...' : 'Log in'}
                </button>

                <p className="text-[11px] text-black/45 dark:text-white/60 leading-snug pt-1">
                  By continuing, you agree to Pluckr’s Terms of Service, Privacy Policy and Cookie Use.
                </p>

                <div className="pt-1 space-y-2 text-[12px] text-black/60 dark:text-white/70">
                  <div>
                    Don’t have an account?{' '}
                    <Link
                      to="/signup"
                      className="font-semibold text-black/75 dark:text-white hover:text-black dark:hover:text-white"
                    >
                      Sign up ›
                    </Link>
                  </div>
                  <div>
                    Forgot your password?{' '}
                    <Link
                      to="/forgot-password"
                      className="font-semibold text-black/75 dark:text-white hover:text-black dark:hover:text-white"
                    >
                      Recover ›
                    </Link>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
